<template>
  <v-card class="my-3">
    <v-card-title>
      <h5>{{ $tt('caster.summaryTitle') }}</h5>
      <v-chip class="ml-3" small color="primary" v-if="isLive">{{ $tt('caster.receiving') }}</v-chip>
      <v-chip class="ml-3" small v-else-if="snapshot">{{ $tt('caster.lastSnapshotNote') }}</v-chip>
    </v-card-title>
    <v-card-text v-if="!snapshot">
      <v-sheet class="body-1 mx-3">({{ $tt('caster.noStatsData') }})</v-sheet>
    </v-card-text>
    <v-card-text v-if="snapshot">
      <v-row dense>
        <v-col cols="6" sm="3" v-for="item in summaryItems" :key="item.label">
          <v-sheet class="pa-2 text-center" outlined rounded>
            <div class="caption grey--text">{{ item.label }}</div>
            <div class="subtitle-1">{{ item.value }}</div>
          </v-sheet>
        </v-col>
      </v-row>

      <!-- システム別観測状況 -->
      <v-simple-table dense class="mt-4" v-if="gnssRows.length !== 0">
        <template v-slot:default>
          <thead>
            <tr>
              <th class="text-left">GNSS</th>
              <th class="text-right">{{ $tt('caster.satellites') }}</th>
              <th class="text-right">{{ $tt('caster.signals') }}</th>
              <th class="text-right">{{ $tt('caster.meanCnr') }} (dB-Hz)</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in gnssRows" :key="row.system">
              <td>{{ row.system }}</td>
              <td class="text-right">{{ row.satelliteCount }}</td>
              <td class="text-right">{{ row.signalCount }}</td>
              <td class="text-right">{{ row.meanCnr }}</td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>

      <!-- メッセージ種別 -->
      <v-simple-table dense class="mt-4">
        <template v-slot:default>
          <thead>
            <tr>
              <th class="text-left">{{ $tt('caster.msgType') }}</th>
              <th class="text-right">{{ $tt('caster.msgCount') }}</th>
              <th class="text-right">{{ $tt('caster.msgInterval') }}</th>
              <th class="text-right">{{ $tt('caster.msgLastSeen') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in messageRows" :key="row.type">
              <td>{{ row.type }}<span class="grey--text" v-if="row.undecoded"> *</span></td>
              <td class="text-right">{{ row.count }}</td>
              <td class="text-right">{{ row.interval }}</td>
              <td class="text-right">{{ row.lastSeen }}</td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>

      <div class="caption grey--text mt-2" v-if="positionText">
        {{ $tt('caster.basePosition') }}: {{ positionText }}
        <span v-if="antennaText"> / {{ $tt('caster.antenna') }}: {{ antennaText }}</span>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import moment from 'moment-timezone'
import i18n from '@/lib/i18n'

/**
 * 基準局の受信サマリ (CasterStatsSnapshot の現在値表示)
 */
export default {
  name: 'CasterStatsSummary',
  props: {
    snapshot: { type: Object, default: null }, // CasterStatsSnapshot
    isLive: { type: Boolean, default: false },
  },
  computed: {
    summaryItems() {
      const snapshot = this.snapshot
      if (!snapshot) {
        return []
      }
      const epoch =
        snapshot.epochIntervalMs !== null && snapshot.epochIntervalMs !== undefined
          ? `${Math.round(snapshot.epochIntervalMs)} ms`
          : '--'
      return [
        { label: i18n.tt('caster.bytesRate'), value: `${Math.round(snapshot.bytesInPerSecond)} B/s` },
        { label: i18n.tt('caster.messagesPerSecond'), value: `${snapshot.messagesPerSecond.toFixed(1)} /s` },
        { label: i18n.tt('caster.epochInterval'), value: epoch },
        { label: i18n.tt('caster.uptime'), value: this.formatUptime(snapshot.uptimeSeconds) },
        { label: i18n.tt('caster.decodeErrors'), value: `${snapshot.decodeErrors}` },
      ]
    },
    gnssRows() {
      const gnss = (this.snapshot && this.snapshot.gnss) || {}
      return Object.keys(gnss).map(system => {
        const stat = gnss[system]
        return {
          system,
          satelliteCount: stat.satelliteCount,
          signalCount: stat.signalCount,
          meanCnr: stat.meanCnr !== null && stat.meanCnr !== undefined ? stat.meanCnr.toFixed(1) : '--',
        }
      })
    },
    messageRows() {
      const types = (this.snapshot && this.snapshot.messageTypes) || {}
      return Object.keys(types)
        .sort((a, b) => Number(a) - Number(b))
        .map(type => {
          const stat = types[type]
          return {
            type,
            undecoded: !!stat.undecoded,
            count: stat.count,
            interval: stat.intervalMs !== undefined ? `${Math.round(stat.intervalMs)} ms` : '--',
            lastSeen: moment(stat.lastSeenAt).format('HH:mm:ss'),
          }
        })
    },
    positionText() {
      const position = this.snapshot && this.snapshot.position
      if (!position) {
        return null
      }
      return `${position.lat.toFixed(5)}, ${position.lon.toFixed(5)} (${position.height.toFixed(1)}m)`
    },
    antennaText() {
      const antenna = this.snapshot && this.snapshot.antenna
      if (!antenna || !antenna.descriptor) {
        return null
      }
      return antenna.descriptor
    },
  },
  methods: {
    formatUptime(seconds) {
      if (seconds === undefined || seconds === null) {
        return '--'
      }
      return moment.duration(seconds, 'seconds').format('d[d] h[h] m[m]', { trim: 'both mid' }) || '0m'
    },
  },
}
</script>
